/**
 * Optional alpha lets callers preserve transparency through a transform.
 * 0 = fully transparent, 1 = fully opaque, undefined = treat as 1 (opaque).
 * The pure-color helpers (rgbToLab/hslToRgb/etc.) ignore alpha; only
 * parseColor and rgbToHex round-trip it.
 */
export type RGB = { r: number; g: number; b: number; a?: number };
export type LAB = { L: number; a: number; b: number };
export type HSL = { h: number; s: number; l: number };

const NAMED_COLORS: Record<string, RGB> = {
  black: { r: 0, g: 0, b: 0 },
  white: { r: 255, g: 255, b: 255 },
  red: { r: 255, g: 0, b: 0 },
  green: { r: 0, g: 128, b: 0 },
  blue: { r: 0, g: 0, b: 255 },
  yellow: { r: 255, g: 255, b: 0 },
  cyan: { r: 0, g: 255, b: 255 },
  magenta: { r: 255, g: 0, b: 255 },
  gray: { r: 128, g: 128, b: 128 },
  grey: { r: 128, g: 128, b: 128 },
  silver: { r: 192, g: 192, b: 192 },
  maroon: { r: 128, g: 0, b: 0 },
  olive: { r: 128, g: 128, b: 0 },
  lime: { r: 0, g: 255, b: 0 },
  aqua: { r: 0, g: 255, b: 255 },
  teal: { r: 0, g: 128, b: 128 },
  navy: { r: 0, g: 0, b: 128 },
  fuchsia: { r: 255, g: 0, b: 255 },
  purple: { r: 128, g: 0, b: 128 },
  orange: { r: 255, g: 165, b: 0 },
  pink: { r: 255, g: 192, b: 203 },
  brown: { r: 165, g: 42, b: 42 },
};

export function parseColor(raw: string): RGB | null {
  const s = raw.trim().toLowerCase();

  if (s === "transparent") {
    return { r: 0, g: 0, b: 0, a: 0 };
  }
  if (NAMED_COLORS[s]) {
    return { ...NAMED_COLORS[s] };
  }

  const hexMatch = s.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/);
  if (hexMatch) {
    const hex = hexMatch[1]!;
    if (hex.length === 3 || hex.length === 4) {
      return {
        r: Number(`0x${hex[0]}${hex[0]}`),
        g: Number(`0x${hex[1]}${hex[1]}`),
        b: Number(`0x${hex[2]}${hex[2]}`),
        ...(hex.length === 4
          ? { a: Number(`0x${hex[3]}${hex[3]}`) / 255 }
          : {}),
      };
    }
    return {
      r: Number(`0x${hex.slice(0, 2)}`),
      g: Number(`0x${hex.slice(2, 4)}`),
      b: Number(`0x${hex.slice(4, 6)}`),
      ...(hex.length === 8 ? { a: Number(`0x${hex.slice(6, 8)}`) / 255 } : {}),
    };
  }

  // `rgba?()` with optional alpha as the 4th channel (number 0-1 or
  // percentage). Keeping the channel counts loose so `rgb(...)` (no alpha)
  // and `rgba(...)` both parse.
  const rgbMatch = s.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9.]+%?))?\s*\)/,
  );
  if (rgbMatch) {
    const result: RGB = {
      r: Math.min(255, Number(rgbMatch[1])),
      g: Math.min(255, Number(rgbMatch[2])),
      b: Math.min(255, Number(rgbMatch[3])),
    };
    if (rgbMatch[4] !== undefined) {
      const raw4 = rgbMatch[4];
      const alpha = raw4.endsWith("%")
        ? Number(raw4.slice(0, -1)) / 100
        : Number(raw4);
      if (Number.isFinite(alpha)) {
        result.a = Math.max(0, Math.min(1, alpha));
      }
    }
    return result;
  }

  return null;
}

export function rgbToHex(c: RGB): string {
  const toHex = (n: number) =>
    Math.round(Math.max(0, Math.min(255, n)))
      .toString(16)
      .padStart(2, "0");
  const base = `#${toHex(c.r)}${toHex(c.g)}${toHex(c.b)}`;
  // Emit 8-digit hex (#RRGGBBAA) when alpha is not fully opaque so callers
  // round-tripping `rgba(0,0,0,0)` or `#ffffff00` don't silently lose the
  // transparency. Fully-opaque (a===1 or undefined) stays 6-digit so the
  // happy path output is unchanged.
  if (c.a !== undefined && c.a < 1) {
    return `${base}${toHex(c.a * 255)}`;
  }
  return base;
}

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c: number): number {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.round(Math.max(0, Math.min(255, v * 255)));
}

const D65_X = 0.95047;
const D65_Y = 1.0;
const D65_Z = 1.08883;

export function rgbToLab(c: RGB): LAB {
  const lr = srgbToLinear(c.r);
  const lg = srgbToLinear(c.g);
  const lb = srgbToLinear(c.b);

  let x = (0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb) / D65_X;
  let y = (0.2126729 * lr + 0.7151522 * lg + 0.072175 * lb) / D65_Y;
  let z = (0.0193339 * lr + 0.119192 * lg + 0.9503041 * lb) / D65_Z;

  const epsilon = 216 / 24389;
  const kappa = 24389 / 27;

  x = x > epsilon ? Math.cbrt(x) : (kappa * x + 16) / 116;
  y = y > epsilon ? Math.cbrt(y) : (kappa * y + 16) / 116;
  z = z > epsilon ? Math.cbrt(z) : (kappa * z + 16) / 116;

  return {
    L: 116 * y - 16,
    a: 500 * (x - y),
    b: 200 * (y - z),
  };
}

export function labToRgb(lab: LAB): RGB {
  const fy = (lab.L + 16) / 116;
  const fx = lab.a / 500 + fy;
  const fz = fy - lab.b / 200;

  const epsilon = 216 / 24389;
  const kappa = 24389 / 27;

  const xr = fx * fx * fx > epsilon ? fx * fx * fx : (116 * fx - 16) / kappa;
  const yr =
    lab.L > kappa * epsilon ? Math.pow((lab.L + 16) / 116, 3) : lab.L / kappa;
  const zr = fz * fz * fz > epsilon ? fz * fz * fz : (116 * fz - 16) / kappa;

  const x = xr * D65_X;
  const y = yr * D65_Y;
  const z = zr * D65_Z;

  const lr = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z;
  const lg = -0.969266 * x + 1.8760108 * y + 0.041556 * z;
  const lb = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z;

  return {
    r: linearToSrgb(lr),
    g: linearToSrgb(lg),
    b: linearToSrgb(lb),
  };
}

export function rgbToHsl(c: RGB): HSL {
  const r = c.r / 255;
  const g = c.g / 255;
  const b = c.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) {
    return { h: 0, s: 0, l };
  }

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  return { h, s, l };
}

function hue2rgb(p: number, q: number, t: number): number {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

export function hslToRgb(c: HSL): RGB {
  if (c.s === 0) {
    const v = Math.round(c.l * 255);
    return { r: v, g: v, b: v };
  }

  const q = c.l < 0.5 ? c.l * (1 + c.s) : c.l + c.s - c.l * c.s;
  const p = 2 * c.l - q;

  return {
    r: Math.round(hue2rgb(p, q, c.h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, c.h) * 255),
    b: Math.round(hue2rgb(p, q, c.h - 1 / 3) * 255),
  };
}

export function rgbBrightness(c: RGB): number {
  return 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
}

export function relativeLuminance(c: RGB): number {
  return (
    0.2126 * srgbToLinear(c.r) +
    0.7152 * srgbToLinear(c.g) +
    0.0722 * srgbToLinear(c.b)
  );
}

export function contrastRatio(c1: RGB, c2: RGB): number {
  const l1 = relativeLuminance(c1);
  const l2 = relativeLuminance(c2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function clampLab(lab: LAB): LAB {
  return {
    L: Math.max(0, Math.min(100, lab.L)),
    a: lab.a,
    b: lab.b,
  };
}
